import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import { useIntl, FormattedMessage } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import sh from "bundles/common/utils/sh";
import trackEvent from "bundles/common/utils/trackEvent";
import engagementTracker from "bundles/Recruiter/engagement/engagementTracker";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import CVThequeSearchBar from "./CVThequeContent/CVThequeSearchBar";
import CVThequeResultsCount from "./CVThequeContent/CVThequeResultsCount";
import CVThequeSortControl from "./CVThequeContent/CVThequeSortControl";
import CVThequeActiveFilters from "./CVThequeContent/CVThequeActiveFilters";
import CVThequeCandidateList from "./CVThequeContent/CVThequeCandidateList";
import CVThequeCandidateDetail from "./CVThequeContent/CVThequeCandidateDetail";
import FiltersCandidatesModal from "./CVThequeContent/FiltersCandidatesModal";
import CandidatesMap from "./CVThequeContent/CandidatesMap";
import { be_regions, lu_regions, fr_regions } from "bundles/common/utils/constants";
import { searchPlaces, staticMapUrl } from "bundles/common/utils/locationCoordinates";
import { List as ListIcon, Map as MapIcon } from "lucide-react";

const LANGUAGE_SKILLS_BY_CODE = {
  fr: "french",
  nl: "dutch",
  en: "english",
};

/** Full-screen loader for initial load, search-query changes, and modal filter apply; delay avoids flashes on fast responses. */
const LOADER_SHOW_DELAY_MS = 200;
const NO_MAP_CANDIDATES = [];
const TEASER_MAP_URL = staticMapUrl({
  pins: [
    { lat: 50.85, lng: 4.35 }, { lat: 51.22, lng: 4.40 }, { lat: 51.05, lng: 3.72 },
    { lat: 50.63, lng: 5.57 }, { lat: 50.47, lng: 4.87 }, { lat: 50.41, lng: 4.44 },
    { lat: 50.88, lng: 4.70 }, { lat: 50.45, lng: 3.95 }, { lat: 51.21, lng: 3.22 },
    { lat: 50.93, lng: 5.34 }, { lat: 50.72, lng: 4.61 }, { lat: 49.61, lng: 6.13 },
  ],
});

const isAxiosCancelError = (err) => {
  if (!err) return false;
  // axios.CancelToken sets a non-standard `__CANCEL__` flag; we also match the message we set.
  return Boolean(err.__CANCEL__) || err?.message?.includes("Request canceled due to new request");
};

const locationsForSearch = (locations) => (locations || []).flatMap((loc) => {
  if (!loc) return [];
  if (loc.type === "town") {
    return loc.lat != null && loc.lng != null ? [{ type: "town", label: loc.label, lat: loc.lat, lng: loc.lng }] : [];
  }
  if (loc.type === "country") return [{ type: "country", code: loc.countryCode }];
  return [{ type: "region", code: loc.code }];
});

const locationsForPersist = (locations) => (locations || []).flatMap((loc) => {
  if (!loc) return [];
  if (loc.type === "town") return [{ type: "town", label: loc.label }];
  if (loc.type === "country") return [{ type: "country", code: loc.countryCode }];
  return [{ type: "region", code: loc.code }];
});

const withoutCoordinates = (filters) => {
  if (!filters || typeof filters !== "object" || !Array.isArray(filters.locations)) return filters;
  return {
    ...filters,
    locations: filters.locations.map((loc) => ({ type: loc.type, code: loc.code, label: loc.label })),
  };
};

const FAKE_CANDIDATES = Array.from({ length: 10 }, (_, i) => ({
  id: `fake-${i}`,
  first_name: ["Jean", "Marie", "Pierre", "Sophie", "Luc", "Emma", "Thomas", "Julie", "Marc", "Claire"][i],
  last_name: ["Dupont", "Martin", "Bernard", "Durand", "Leroy", "Moreau", "Laurent", "Simon", "Michel", "Garcia"][i],
  job_title: ["Développeur", "Chef de projet", "Designer", "Comptable", "Consultant", "Analyste", "Ingénieur", "Manager", "Assistant", "Coordinateur"][i],
  email: "exemple@squarehub.com",
  phone: "+32 000 00 00 00",
  availability: ["looking", "listening", "currently_employed"][i % 3],
  region: ["brussels", "walloon_brabant", "flemish_brabant", "liege", "hainaut", "namur", "luxembourg_be", "antwerp", "east_flanders", "west_flanders"][i],
  city: null,
  experience_year: ["1", "3", "5", "2", "7", "4", "6", "8", "1", "10"][i],
  candidate_status: "employee",
  profile_picture_url: null,
  resume_url: null,
}));

const CVThequeContent = ({ setKanban }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const intl = useIntl();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const navigate = useNavigate();

  const authenticity_token = useSelector((state) => state.authenticity_token);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const favorites = currentRecruiter?.favorite_candidate_ids || [];

  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [firstSelected, setFirstSelected] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [count, setCount] = useState(null);
  const [freshInArea, setFreshInArea] = useState(null);
  const [freshInAreaDays, setFreshInAreaDays] = useState(30);

  const [onlyFavorites, setOnlyFavorites] = useState(() => {
    const params = new URLSearchParams(window.location.search || "");
    const raw = params.get("onlyFavorites") ?? params.get("only_favorites");
    return raw === "true" || raw === "1";
  });

  const [queryText, setQueryText] = useState("");
  const [booleanClauses, setBooleanClauses] = useState([]);
  const [booleanExpression, setBooleanExpression] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [sortBy, setSortBy] = useState("newest");

  const [filtersCandidatesOpen, setFiltersCandidatesOpen] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedAvailabilities, setSelectedAvailabilities] = useState([]);
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [selectedExperiences, setSelectedExperiences] = useState([]);
  const [selectedHardSkills, setSelectedHardSkills] = useState([]);
  const [selectedSoftSkills, setSelectedSoftSkills] = useState([]);
  const [selectedHighestDegrees, setSelectedHighestDegrees] = useState([]);
  const [selectedCandidateStatuses, setSelectedCandidateStatuses] = useState([]);
  const [selectedActiveWithin, setSelectedActiveWithin] = useState(null);
  const [selectedRadiusKm, setSelectedRadiusKm] = useState(0);
  const [viewMode, setViewMode] = useState("list");
  const [mapCandidates, setMapCandidates] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapDetailOpen, setMapDetailOpen] = useState(false);
  const [mapBleed, setMapBleed] = useState(0);
  const mapColumnRef = useRef(null);

  // Boolean so REPLACE_CURRENT_RECRUITER does not change reference and retrigger fetches.
  const resumeLibraryCredit = Boolean(currentRecruiter?.resume_library_access);
  // Monotonic seq: only the latest search request may toggle the full-screen loader off.
  // (In-flight counters break when sh cancels a prior request and that promise never settled.)
  const searchRequestSeqRef = useRef(0);
  const loaderTimerRef = useRef(null);
  const skipInitialPageOnlyFetchRef = useRef(true);
  const noCreditCountRequestSeqRef = useRef(0);
  const hasCompletedInitialCvFetchRef = useRef(false);
  const prevQueryTextForLoaderRef = useRef(null);
  const prevFiltersKeyForLoaderRef = useRef(null);
  const prevOnlyFavoritesRef = useRef(onlyFavorites);
  const detailPanelRef = useRef(null);
  const hasHydratedFiltersRef = useRef(false);
  const persistFiltersTimerRef = useRef(null);

  const buildAllRegions = useCallback(() => {
    const beRegions = be_regions.map((region) => ({
      code: region,
      label: intl.formatMessage({ id: `regions.${region}` }),
      country: intl.formatMessage({ id: "regions.belgium", defaultMessage: "Belgium" }),
      type: "region",
    }));
    const luRegions = lu_regions.map((region) => ({
      code: region,
      label: intl.formatMessage({ id: `regions.${region}` }),
      country: intl.formatMessage({ id: "regions.luxembourg", defaultMessage: "Luxembourg" }),
      type: "region",
    }));
    const frRegions = fr_regions.map((region) => ({
      code: region,
      label: intl.formatMessage({ id: `regions.${region}` }),
      country: intl.formatMessage({ id: "regions.france", defaultMessage: "France" }),
      type: "region",
    }));

    const belgiumLabel = intl.formatMessage({ id: "regions.belgium", defaultMessage: "Belgium" });
    const luxembourgLabel = intl.formatMessage({ id: "regions.luxembourg", defaultMessage: "Luxembourg" });
    const franceLabel = intl.formatMessage({ id: "regions.france", defaultMessage: "France" });
    const countries = [
      { code: "__country_belgium", countryCode: "belgium", label: belgiumLabel, country: belgiumLabel, type: "country", regionCodes: be_regions },
      { code: "__country_luxembourg", countryCode: "luxembourg", label: luxembourgLabel, country: luxembourgLabel, type: "country", regionCodes: lu_regions },
      { code: "__country_france", countryCode: "france", label: franceLabel, country: franceLabel, type: "country", regionCodes: fr_regions },
    ];

    return [...countries, ...beRegions, ...luRegions, ...frRegions].sort((a, b) => {
      if (a.country !== b.country) return a.country.localeCompare(b.country);
      if (a.type !== b.type) return a.type === "country" ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
  }, [intl]);

  const locationOptions = useMemo(() => buildAllRegions(), [buildAllRegions]);

  const createFilters = useCallback(() => {
    let filters = {};
    const languageSkills = (selectedLanguages || [])
      .map((languageCode) => LANGUAGE_SKILLS_BY_CODE[languageCode])
      .filter(Boolean);

    if (onlyFavorites) {
      Object.assign(filters, { only_favorites: true });
    }

    const locations = locationsForSearch(selectedLocations);
    if (locations.length > 0) {
      Object.assign(filters, { locations });
      if (selectedRadiusKm > 0) {
        Object.assign(filters, { radius_km: selectedRadiusKm });
      }
    }
    if (selectedAvailabilities?.length > 0) {
      Object.assign(filters, { availability: selectedAvailabilities });
    }
    if (selectedSectors?.length > 0) {
      Object.assign(filters, { desired_sector: selectedSectors });
    }
    if (selectedExperiences?.length > 0) {
      let experienceArray = [];
      for (let i = 0; i < selectedExperiences.length; i++) {
        experienceArray = experienceArray.concat(selectedExperiences[i] || []);
      }
      Object.assign(filters, { experience_year: experienceArray.map(String) });
    }
    if (selectedHardSkills?.length > 0 || selectedSoftSkills?.length > 0 || languageSkills.length > 0) {
      Object.assign(filters, {
        skills: (selectedHardSkills || []).concat(selectedSoftSkills || []).concat(languageSkills),
      });
    }
    if (selectedHighestDegrees?.length > 0) {
      Object.assign(filters, { highest_degree: selectedHighestDegrees });
    }
    if (selectedCandidateStatuses?.length > 0) {
      Object.assign(filters, { candidate_status: selectedCandidateStatuses });
    }
    if (selectedActiveWithin) {
      Object.assign(filters, { active_within: selectedActiveWithin });
    }
    return filters;
  }, [
    onlyFavorites,
    selectedLanguages,
    selectedLocations,
    selectedAvailabilities,
    selectedSectors,
    selectedExperiences,
    selectedHardSkills,
    selectedSoftSkills,
    selectedHighestDegrees,
    selectedCandidateStatuses,
    selectedActiveWithin,
    selectedRadiusKm,
  ]);

  const serializeFiltersForPersist = useCallback(() => {
    return {
      query: queryText || "",
      sort: sortBy,
      only_favorites: onlyFavorites,
      locations: locationsForPersist(selectedLocations),
      radius_km: selectedRadiusKm,
      languages: selectedLanguages || [],
      availabilities: selectedAvailabilities || [],
      sectors: selectedSectors || [],
      experiences: selectedExperiences || [],
      hard_skills: selectedHardSkills || [],
      soft_skills: selectedSoftSkills || [],
      highest_degrees: selectedHighestDegrees || [],
      candidate_statuses: selectedCandidateStatuses || [],
      active_within: selectedActiveWithin,
      boolean_clauses: booleanClauses || [],
    };
  }, [
    queryText,
    sortBy,
    onlyFavorites,
    selectedLocations,
    selectedLanguages,
    selectedAvailabilities,
    selectedSectors,
    selectedExperiences,
    selectedHardSkills,
    selectedSoftSkills,
    selectedHighestDegrees,
    selectedCandidateStatuses,
    selectedActiveWithin,
    selectedRadiusKm,
    booleanClauses,
  ]);

  const clearLoaderTimer = useCallback(() => {
    if (loaderTimerRef.current) {
      window.clearTimeout(loaderTimerRef.current);
      loaderTimerRef.current = null;
    }
  }, []);

  const fetchCandidates = useCallback(
    (
      currentPage = 1,
      query = queryText,
      filters = createFilters(),
      { showLoader = false, trackSearch = false } = {}
    ) => {
      // Like JobsContent: wait until recruiter is hydrated before treating missing
      // resume_library_access as "no credit" (undefined would wrongly show fake data + hide loader).
      if (!currentRecruiter) {
        // Bump the seq too, so a still-pending search cannot resolve and turn this loader back off.
        clearLoaderTimer();
        searchRequestSeqRef.current += 1;
        setLoading(true);
        return;
      }
      if (!resumeLibraryCredit) {
        clearLoaderTimer();
        searchRequestSeqRef.current += 1;
        setLoading(false);
        setCandidates(FAKE_CANDIDATES);
        setSelectedCandidate(FAKE_CANDIDATES[0]);
        setPageCount(1);
        // Real total comes from the API even without library access (see SearchController#candidates).
        noCreditCountRequestSeqRef.current += 1;
        const countReqSeq = noCreditCountRequestSeqRef.current;
        const serializedFiltersForCount =
          typeof filters === "string" ? filters : JSON.stringify(filters || {});
        sh
          .get("search/candidates", {
            cancelKey: "cvtheque-candidates-count",
            params: {
              authenticity_token,
              query,
              page: 1,
              filters: serializedFiltersForCount,
              boolean_clauses: JSON.stringify(booleanClauses || []),
              boolean_expression: (booleanExpression || "").trim(),
            },
          })
          .then((res) => {
            if (countReqSeq !== noCreditCountRequestSeqRef.current) return;
            const noCreditCount =
              !query &&
              (booleanClauses || []).length === 0 &&
              !(booleanExpression || "").trim() &&
              Object.keys(filters).length === 0
                ? res?.data?.candidate_count || 0
                : res?.data?.pagy?.count || 0;
            setCount(noCreditCount);
            if (trackSearch) {
              const searchProperties = {
                query: query || "",
                filters: typeof filters === "string" ? filters : withoutCoordinates(filters || {}),
                result_count: noCreditCount,
                has_credit: false,
              };
              engagementTracker.startSearch(searchProperties);
              trackEvent("cvtheque", "searched", searchProperties);
            }
          })
          .catch((err) => {
            if (isAxiosCancelError(err)) return;
            console.error(err);
          });
        return;
      }

      searchRequestSeqRef.current += 1;
      const requestSeq = searchRequestSeqRef.current;
      clearLoaderTimer();
      if (showLoader) {
        loaderTimerRef.current = window.setTimeout(() => {
          if (requestSeq === searchRequestSeqRef.current) {
            setLoading(true);
          }
        }, LOADER_SHOW_DELAY_MS);
      }

      const serializedFilters =
        typeof filters === "string" ? filters : JSON.stringify(filters || {});
      sh.get("search/candidates", {
        cancelKey: "cvtheque-candidates",
        params: {
          authenticity_token,
          query,
          page: currentPage,
          filters: serializedFilters,
          boolean_clauses: JSON.stringify(booleanClauses || []),
          sort: sortBy,
          boolean_expression: (booleanExpression || "").trim(),
        },
      })
        .then((res) => {
          if (requestSeq !== searchRequestSeqRef.current) return;
          hasCompletedInitialCvFetchRef.current = true;
          setCandidates(res.data?.data || []);
          if (res.data?.data?.length > 0) {
            setFirstSelected(false);
            setSelectedCandidate(res.data.data[0]);
          } else {
            setSelectedCandidate(null);
          }
          setPageCount(res.data?.pagy?.pages || 1);
          setFreshInArea(res?.data?.fresh_in_area_count ?? null);
          if (res?.data?.fresh_in_area_days) setFreshInAreaDays(res.data.fresh_in_area_days);
          const resultCount =
            !query &&
            (booleanClauses || []).length === 0 &&
            !(booleanExpression || "").trim() &&
            Object.keys(filters).length === 0
              ? res?.data?.candidate_count || 0
              : res?.data?.pagy?.count || 0;
          setCount(resultCount);
          if (trackSearch) {
            const searchProperties = {
              query: query || "",
              filters: typeof filters === "string" ? filters : withoutCoordinates(filters || {}),
              result_count: resultCount,
              has_credit: true,
            };
            engagementTracker.startSearch(searchProperties);
            trackEvent("cvtheque", "searched", searchProperties);
          }
        })
        .catch((err) => {
          if (isAxiosCancelError(err)) return;
          console.error(err);
        })
        .finally(() => {
          // Only the latest request owns the loader. Cancelled/superseded ones must not
          // leave loading stuck, and must not clear a newer in-flight search's loader.
          if (requestSeq !== searchRequestSeqRef.current) return;
          clearLoaderTimer();
          setLoading(false);
        });
    },
    [
      authenticity_token,
      queryText,
      createFilters,
      resumeLibraryCredit,
      currentRecruiter,
      booleanClauses,
      sortBy,
      booleanExpression,
      clearLoaderTimer,
    ]
  );

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCandidateViewed = useCallback((candidate) => {
    setSelectedCandidate(candidate);
    if (candidate?.id) {
      trackEvent("cvtheque", "candidate_viewed", { candidate_id: candidate.id });
    }
  }, []);

  const fetchMapCandidates = useCallback(() => {
    setMapLoading(true);
    sh.get("search/map_candidates", {
      cancelKey: "cvtheque-map-candidates",
      params: {
        authenticity_token,
        query: queryText,
        filters: JSON.stringify(createFilters()),
        boolean_clauses: JSON.stringify(booleanClauses || []),
        boolean_expression: (booleanExpression || "").trim(),
        sort: sortBy,
      },
    })
      .then((res) => {
        setMapCandidates(res.data?.data || []);
        setMapLoading(false);
      })
      .catch((err) => {
        if (isAxiosCancelError(err)) return;
        console.error(err);
        setMapCandidates([]);
        setMapLoading(false);
      });
  }, [authenticity_token, queryText, createFilters, booleanClauses, booleanExpression, sortBy]);

  useEffect(() => {
    if (viewMode !== "map" || !resumeLibraryCredit) return;
    fetchMapCandidates();
  }, [viewMode, resumeLibraryCredit, fetchMapCandidates]);

  useEffect(() => {
    if (viewMode !== "map") return undefined;
    const measure = () => {
      const el = mapColumnRef.current;
      if (!el || !isDesktop) {
        setMapBleed(0);
        return;
      }
      const gap = document.documentElement.clientWidth - el.getBoundingClientRect().right;
      setMapBleed(Math.max(0, gap - 48));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [viewMode, isDesktop, resumeLibraryCredit]);

  const handleMapCandidateSelect = useCallback((candidateId) => {
    sh.get(`search/map_candidates/${candidateId}`, {
      cancelKey: "cvtheque-map-candidate",
      params: { authenticity_token, filters: JSON.stringify(createFilters()) },
    })
      .then((res) => {
        if (res.data?.data) {
          handleCandidateViewed(res.data.data);
          setMapDetailOpen(true);
        }
      })
      .catch((err) => {
        if (isAxiosCancelError(err)) return;
        console.error(err);
      });
  }, [authenticity_token, createFilters, handleCandidateViewed]);

  const clearAllFilters = useCallback(() => {
    engagementTracker.clearSearch();
    setQueryText("");
    setOnlyFavorites(false);
    setSelectedLocations([]);
    setSelectedRadiusKm(0);
    setBooleanClauses([]);
    setSelectedLanguages([]);
    setSelectedAvailabilities([]);
    setSelectedSectors([]);
    setSelectedExperiences([]);
    setSelectedHardSkills([]);
    setSelectedSoftSkills([]);
    setSelectedHighestDegrees([]);
    setSelectedCandidateStatuses([]);
    setSelectedActiveWithin(null);
  }, []);

  const handleFavoriteToggle = useCallback((candidateId, action) => {
    const candidateKey = `${candidateId}`;
    const favoriteKeys = (favorites || []).map((id) => `${id}`);
    const isFavorite = favoriteKeys.includes(candidateKey);

    if (action === "reject") {
      const nextCandidates = (candidates || []).filter((c) => `${c?.id}` !== candidateKey);
      setCandidates(nextCandidates);
      setCount((prev) => (prev == null ? prev : Math.max(prev - 1, 0)));
      setSelectedCandidate((prevSelected) => {
        if (!prevSelected) return prevSelected;
        if (`${prevSelected?.id}` !== candidateKey) return prevSelected;
        return nextCandidates[0] || null;
      });

      sh.put("/recruiter/favorite_candidate", {
        authenticity_token,
        candidate_id: candidateId,
        status: "rejected",
      }).then((res) => {
        if (res.data) {
          dispatch({ type: "UPDATE_CURRENT_RECRUITER", payload: res.data });
        }
      }).catch((err) => {
        console.error(err);
      });

      return;
    }

    if (action !== "toggle") return;

    const updatedFavorites = isFavorite
      ? (favorites || []).filter((id) => `${id}` !== candidateKey)
      : [...(favorites || []), candidateId];

    if (onlyFavorites && isFavorite) {
      const nextCandidates = (candidates || []).filter(
        (c) => `${c?.id}` !== candidateKey
      );
      setCandidates(nextCandidates);
      setCount((prev) => (prev == null ? prev : Math.max(prev - 1, 0)));

      setSelectedCandidate((prevSelected) => {
        if (!prevSelected) return prevSelected;
        if (`${prevSelected?.id}` !== candidateKey) return prevSelected;
        return isDesktop ? (nextCandidates[0] || null) : null;
      });
    }

    sh.put("update_favorite_candidates", {
      authenticity_token,
      favorite_candidates: updatedFavorites,
    }).then((res) => {
      if (res.data) {
        dispatch({ type: "UPDATE_CURRENT_RECRUITER", payload: res.data });
      }
    });
  }, [authenticity_token, candidates, dispatch, favorites, isDesktop, onlyFavorites]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const raw = params.get("onlyFavorites") ?? params.get("only_favorites");
    const nextOnlyFavorites = raw === "true" || raw === "1";
    setOnlyFavorites((prev) => (prev === nextOnlyFavorites ? prev : nextOnlyFavorites));
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    if (onlyFavorites) {
      params.set("onlyFavorites", "true");
      params.delete("only_favorites");
    } else {
      params.delete("onlyFavorites");
      params.delete("only_favorites");
    }

    const nextSearch = params.toString();
    const currentSearch = (location.search || "").replace(/^\?/, "");
    if (nextSearch !== currentSearch) {
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`,
        { replace: true }
      );
    }
  }, [onlyFavorites, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (onlyFavorites && !prevOnlyFavoritesRef.current) {
      trackEvent("cvtheque", "favorites_filtered");
    }
    prevOnlyFavoritesRef.current = onlyFavorites;
  }, [onlyFavorites]);

  useEffect(() => {
    if (hasHydratedFiltersRef.current) return;
    if (!currentRecruiter) return;
    hasHydratedFiltersRef.current = true;

    const saved = currentRecruiter.cv_theque_filters;
    if (!saved || Object.keys(saved).length === 0) return;

    if (typeof saved.query === "string") setQueryText(saved.query);
    if (saved.sort) setSortBy(saved.sort);
    if (Array.isArray(saved.boolean_clauses)) setBooleanClauses(saved.boolean_clauses);
    if (Array.isArray(saved.languages)) setSelectedLanguages(saved.languages);
    if (Array.isArray(saved.availabilities)) setSelectedAvailabilities(saved.availabilities);
    if (Array.isArray(saved.sectors)) setSelectedSectors(saved.sectors);
    if (Array.isArray(saved.experiences)) setSelectedExperiences(saved.experiences);
    if (Array.isArray(saved.hard_skills)) setSelectedHardSkills(saved.hard_skills);
    if (Array.isArray(saved.soft_skills)) setSelectedSoftSkills(saved.soft_skills);
    if (Array.isArray(saved.highest_degrees)) setSelectedHighestDegrees(saved.highest_degrees);
    if (Array.isArray(saved.candidate_statuses)) setSelectedCandidateStatuses(saved.candidate_statuses);
    if (saved.active_within) setSelectedActiveWithin(saved.active_within);
    if (saved.radius_km) setSelectedRadiusKm(Number(saved.radius_km) || 0);

    const options = buildAllRegions();
    const byCode = new Map(options.map((option) => [option.code, option]));
    const byCountry = new Map(
      options.filter((option) => option.type === "country").map((option) => [option.countryCode, option])
    );
    if (Array.isArray(saved.locations) && saved.locations.length > 0) {
      const townsGroupLabel = intl.formatMessage({ id: "recruiter.cv_theque.towns_group", defaultMessage: "Towns" });
      const restored = saved.locations.map((loc) => {
        if (loc?.type === "town" && loc.label) {
          return { code: `town:${loc.label}`, label: loc.label, country: townsGroupLabel, type: "town" };
        }
        if (loc?.type === "country") return byCountry.get(loc.code);
        return byCode.get(loc?.code);
      }).filter(Boolean);
      setSelectedLocations(restored);

      restored.filter((loc) => loc.type === "town").forEach((town) => {
        searchPlaces(town.label, i18nLocale).then((places) => {
          const place = places[0];
          setSelectedLocations((prev) => (prev || []).flatMap((loc) => {
            if (loc.code !== town.code) return [loc];
            return place ? [{ ...loc, lat: place.lat, lng: place.lng }] : [];
          }));
        });
      });
    } else if (Array.isArray(saved.region_codes) && saved.region_codes.length > 0) {
      setSelectedLocations(saved.region_codes.map((code) => byCode.get(code)).filter(Boolean));
    }

    const params = new URLSearchParams(window.location.search || "");
    const rawUrl = params.get("onlyFavorites") ?? params.get("only_favorites");
    if (rawUrl == null) {
      setOnlyFavorites(Boolean(saved.only_favorites));
    }
  }, [currentRecruiter, buildAllRegions, intl]);

  useEffect(() => {
    if (!hasHydratedFiltersRef.current) return;
    if (!currentRecruiter || !resumeLibraryCredit) return;

    if (persistFiltersTimerRef.current) {
      window.clearTimeout(persistFiltersTimerRef.current);
    }
    persistFiltersTimerRef.current = window.setTimeout(() => {
      sh.put("/recruiter/cv_theque_filters", {
        authenticity_token,
        cv_theque_filters: serializeFiltersForPersist(),
      }).catch((err) => {
        console.error(err);
      });
    }, 600);

    return () => {
      if (persistFiltersTimerRef.current) {
        window.clearTimeout(persistFiltersTimerRef.current);
      }
    };
  }, [
    authenticity_token,
    currentRecruiter?.id,
    resumeLibraryCredit,
    serializeFiltersForPersist,
  ]);

  useEffect(() => {
    setPage(1);
    const queryChanged =
      prevQueryTextForLoaderRef.current !== null &&
      prevQueryTextForLoaderRef.current !== queryText;
    const filtersKey = JSON.stringify({
      booleanClauses,
      booleanExpression,
      onlyFavorites,
      selectedLanguages,
      selectedLocations,
      selectedAvailabilities,
      selectedSectors,
      selectedExperiences,
      selectedHardSkills,
      selectedSoftSkills,
      selectedHighestDegrees,
      selectedCandidateStatuses,
      selectedRadiusKm,
      selectedActiveWithin,
      sortBy,
    });
    const filtersChanged =
      prevFiltersKeyForLoaderRef.current !== null &&
      prevFiltersKeyForLoaderRef.current !== filtersKey;
    const showLoader =
      !!currentRecruiter &&
      resumeLibraryCredit &&
      (!hasCompletedInitialCvFetchRef.current || queryChanged || filtersChanged);
    prevQueryTextForLoaderRef.current = queryText;
    prevFiltersKeyForLoaderRef.current = filtersKey;
    fetchCandidates(1, queryText, createFilters(), { showLoader, trackSearch: queryChanged || filtersChanged });
  }, [
    queryText,
    onlyFavorites,
    selectedLanguages,
    selectedLocations,
    selectedAvailabilities,
    selectedSectors,
    selectedExperiences,
    selectedHardSkills,
    selectedSoftSkills,
    selectedHighestDegrees,
    selectedCandidateStatuses,
    selectedRadiusKm,
    selectedActiveWithin,
    booleanClauses,
    sortBy,
    booleanExpression,
    resumeLibraryCredit,
    authenticity_token,
    currentRecruiter?.id,
  ]);

  useEffect(() => {
    if (skipInitialPageOnlyFetchRef.current) {
      skipInitialPageOnlyFetchRef.current = false;
      return;
    }
    fetchCandidates(page, queryText, createFilters(), { showLoader: false });
  }, [page]);

  useEffect(() => {
    // Prevent focus/scroll jump when switching between candidates on the left.
    if (typeof document === "undefined") return;
    try {
      document.activeElement?.blur?.();
    } catch (e) {
      // no-op
    }
  }, [selectedCandidate?.id]);

  useEffect(() => {
    if (selectedCandidate?.id && !firstSelected) {
      setFirstSelected(true);
    }
  }, [selectedCandidate?.id]);

  useEffect(() => {
    if (!isDesktop) return;
    const el = detailPanelRef.current;
    if (!el) return;

    const TOP_OFFSET = 40;
    const BOTTOM_PADDING = 16;
    let currentTop = TOP_OFFSET;
    let lastScrollY = window.scrollY;

    el.style.position = "sticky";
    el.style.alignSelf = "flex-start";
    el.style.top = `${currentTop}px`;

    const onScroll = () => {
      const scrollY = window.scrollY;
      const delta = scrollY - lastScrollY;
      lastScrollY = scrollY;

      const panelHeight = el.offsetHeight;
      const vh = window.innerHeight;

      if (panelHeight <= vh - TOP_OFFSET - BOTTOM_PADDING) {
        currentTop = TOP_OFFSET;
      } else {
        const topMin = vh - panelHeight - BOTTOM_PADDING;
        currentTop = Math.max(topMin, Math.min(TOP_OFFSET, currentTop - delta));
      }

      el.style.top = `${currentTop}px`;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      el.style.position = "";
      el.style.top = "";
      el.style.alignSelf = "";
    };
  }, [isDesktop, selectedCandidate?.id, viewMode]);


  const hasActiveFilters =
    Boolean(queryText) ||
    (selectedLocations?.length || 0) > 0 ||
    (booleanClauses?.length || 0) > 0 ||
    (selectedLanguages?.length || 0) > 0 ||
    (selectedAvailabilities?.length || 0) > 0 ||
    (selectedSectors?.length || 0) > 0 ||
    (selectedExperiences?.length || 0) > 0 ||
    (selectedHardSkills?.length || 0) > 0 ||
    (selectedSoftSkills?.length || 0) > 0 ||
    (selectedHighestDegrees?.length || 0) > 0 ||
    (selectedCandidateStatuses?.length || 0) > 0 ||
    Boolean(selectedActiveWithin) ||
    onlyFavorites;

  const showEmptyState = resumeLibraryCredit && !loading && (candidates?.length || 0) === 0;

  return (
    <Box sx={{ my: "40px" }}>
      <CVThequeSearchBar
        queryText={queryText}
        setQueryText={setQueryText}
        locationOptions={locationOptions}
        selectedLocations={selectedLocations}
        setSelectedLocations={setSelectedLocations}
        selectedRadiusKm={selectedRadiusKm}
        setSelectedRadiusKm={setSelectedRadiusKm}
        onAdvancedFiltersClick={() => {
          setFiltersCandidatesOpen(true);
          trackEvent("cvtheque", "filters_opened");
        }}
      />

      <CVThequeActiveFilters
        selectedLocations={selectedLocations}
        setSelectedLocations={setSelectedLocations}
        selectedRadiusKm={selectedRadiusKm}
        setSelectedRadiusKm={setSelectedRadiusKm}
        onlyFavorites={onlyFavorites}
        setOnlyFavorites={setOnlyFavorites}
        selectedLanguages={selectedLanguages}
        setSelectedLanguages={setSelectedLanguages}
        selectedSectors={selectedSectors}
        setSelectedSectors={setSelectedSectors}
        selectedAvailabilities={selectedAvailabilities}
        setSelectedAvailabilities={setSelectedAvailabilities}
        selectedExperiences={selectedExperiences}
        setSelectedExperiences={setSelectedExperiences}
        selectedHardSkills={selectedHardSkills}
        setSelectedHardSkills={setSelectedHardSkills}
        selectedSoftSkills={selectedSoftSkills}
        setSelectedSoftSkills={setSelectedSoftSkills}
        selectedHighestDegrees={selectedHighestDegrees}
        setSelectedHighestDegrees={setSelectedHighestDegrees}
        selectedCandidateStatuses={selectedCandidateStatuses}
        setSelectedCandidateStatuses={setSelectedCandidateStatuses}
        selectedActiveWithin={selectedActiveWithin}
        setSelectedActiveWithin={setSelectedActiveWithin}
        booleanClauses={booleanClauses}
        setBooleanClauses={setBooleanClauses}
        onClearAll={clearAllFilters}
      />

      {typeof document !== "undefined" &&
        loading &&
        createPortal(
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: (t) => t.zIndex.tooltip,
              backgroundColor: "rgba(255, 255, 255, 0.85)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SquarehubLoader size={72} />
          </Box>,
          document.body
        )}

      <Grid container spacing="40px">
        <Grid item xs={12}>
          <Box
            sx={{
              mt: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {freshInArea > 0 && (
                <Typography
                  sx={{ fontSize: "13px", color: "#16A34A", fontWeight: 600 }}
                >
                  <FormattedMessage
                    id="recruiter.cv_theque.fresh_in_area"
                    defaultMessage="{count, number} new profiles in your search area in the last {days} days"
                    values={{ count: freshInArea, days: freshInAreaDays }}
                  />
                </Typography>
              )}
            </Box>
            <CVThequeResultsCount count={count} />
            <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px" }}>
              <Box
                role="group"
                sx={{
                  display: "flex", p: "3px", gap: "2px",
                  borderRadius: "16px", border: "1px solid hsl(160, 10%, 90%)", bgcolor: "hsl(0, 0%, 100%)",
                }}
              >
                {[
                  { mode: "list", Icon: ListIcon, id: "recruiter.cv_theque.view_list", fallback: "List" },
                  { mode: "map", Icon: MapIcon, id: "recruiter.cv_theque.view_map", fallback: "Map" },
                ].map(({ mode, Icon, id, fallback }) => {
                  const active = viewMode === mode;
                  return (
                    <Box
                      key={mode}
                      component="button"
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        if (active) return;
                        setViewMode(mode);
                        setMapDetailOpen(false);
                        if (mode === "map") trackEvent("cvtheque", "map_used", { has_credit: resumeLibraryCredit });
                      }}
                      sx={{
                        display: "flex", alignItems: "center", gap: "6px",
                        px: "12px", py: "6px", borderRadius: "12px", border: "none",
                        cursor: active ? "default" : "pointer", fontSize: "12px",
                        fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                        bgcolor: active ? "hsl(178, 93%, 10%)" : "transparent",
                        color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 10%, 45%)",
                        transition: "background-color 0.15s, color 0.15s",
                        "&:hover": { color: active ? "hsl(0, 0%, 100%)" : "hsl(178, 93%, 10%)" },
                      }}
                    >
                      <Icon size={14} />
                      <FormattedMessage id={id} defaultMessage={fallback} />
                    </Box>
                  );
                })}
              </Box>
              {resumeLibraryCredit && (
                <CVThequeSortControl value={sortBy} onChange={setSortBy} />
              )}
            </Box>
          </Box>
        </Grid>

        {showEmptyState ? (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                py: 8,
                gap: 1.5,
              }}
            >
              <Typography variant="h5" sx={{ fontFamily: "Segoe UI Semibold, sans-serif" }}>
                <FormattedMessage
                  id={
                    hasActiveFilters
                      ? "recruiter.cv_theque.no_results_title"
                      : "recruiter.cv_theque.no_candidates_yet"
                  }
                />
              </Typography>
              {hasActiveFilters && (
                <>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    <FormattedMessage id="recruiter.cv_theque.no_results_subtitle" />
                  </Typography>
                  <Button
                    onClick={clearAllFilters}
                    sx={{
                      mt: 1,
                      textTransform: "none",
                      borderRadius: "999px",
                      padding: "8px 24px",
                      backgroundColor: theme.palette.text.main,
                      color: theme.palette.common.white,
                      "&:hover": {
                        backgroundColor: theme.palette.text.dark,
                        color: theme.palette.common.white,
                      },
                    }}
                  >
                    <FormattedMessage id="job_search.clear_filters" defaultMessage="Clear filters" />
                  </Button>
                </>
              )}
            </Box>
          </Grid>
        ) : (
          <>
            {viewMode === "map" && resumeLibraryCredit ? (
              <>
                <Grid item xs={12} md={6}>
                  <CVThequeCandidateList
                    candidates={candidates}
                    selectedCandidate={mapDetailOpen ? selectedCandidate : null}
                    setSelectedCandidate={(candidate) => {
                      handleCandidateViewed(candidate);
                      setMapDetailOpen(true);
                    }}
                    page={page}
                    pageCount={pageCount}
                    onPageChange={handlePageChange}
                    favorites={favorites}
                    onFavoriteToggle={handleFavoriteToggle}
                  />
                </Grid>
                <Grid item xs={12} md={6} ref={mapColumnRef}>
                  <Box sx={{ height: { xs: "60vh", md: "calc(100vh - 108px)" }, position: { xs: "relative", md: "sticky" }, top: { md: "88px" }, mr: `-${mapBleed}px` }}>
                    <CandidatesMap
                      candidates={mapCandidates || NO_MAP_CANDIDATES}
                      loading={mapLoading || mapCandidates === null}
                      onSelectCandidate={handleMapCandidateSelect}
                    />
                    {mapDetailOpen && selectedCandidate && (
                      <Box sx={{ position: "absolute", inset: 0, zIndex: 2, overflowY: "auto", bgcolor: "background.default" }}>
                        <CVThequeCandidateDetail
                          selectedCandidate={selectedCandidate}
                          setSelectedCandidate={setSelectedCandidate}
                          resumeLibraryCredit={resumeLibraryCredit}
                          setKanban={setKanban}
                          onClose={() => setMapDetailOpen(false)}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  <Box style={{ filter: resumeLibraryCredit ? "0" : "blur(2.5px)", pointerEvents: resumeLibraryCredit ? "auto" : "none", userSelect: resumeLibraryCredit ? "auto" : "none", height: "100%" }}>
                    <CVThequeCandidateList
                      candidates={candidates}
                      selectedCandidate={selectedCandidate}
                      setSelectedCandidate={handleCandidateViewed}
                      page={page}
                      pageCount={pageCount}
                      onPageChange={handlePageChange}
                      favorites={favorites}
                      onFavoriteToggle={handleFavoriteToggle}
                    />
                  </Box>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  ref={detailPanelRef}
                >
                  {viewMode === "map" && !resumeLibraryCredit ? (
                    <Box sx={{
                      position: "relative", height: { xs: "60vh", md: "calc(100vh - 108px)" },
                      borderRadius: "16px", overflow: "hidden", bgcolor: "hsl(160, 10%, 94%)",
                    }}>
                      {TEASER_MAP_URL && (
                        <Box
                          component="img"
                          src={TEASER_MAP_URL}
                          alt=""
                          sx={{ width: "100%", height: "100%", objectFit: "cover", filter: "blur(3px)", transform: "scale(1.04)" }}
                        />
                      )}
                      <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", p: "24px" }}>
                        <Box sx={{
                          display: "flex", flexDirection: "column", alignItems: "center", gap: "12px",
                          maxWidth: "340px", p: "28px 24px", borderRadius: "16px", textAlign: "center",
                          bgcolor: "hsl(0, 0%, 100%)", boxShadow: "0 8px 24px hsla(178, 93%, 10%, 0.12)",
                        }}>
                          <Box sx={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            width: "44px", height: "44px", borderRadius: "50%",
                            bgcolor: "hsla(153, 68%, 25%, 0.1)", color: "hsl(153, 68%, 25%)",
                          }}>
                            <MapIcon size={22} />
                          </Box>
                          <Typography sx={{ fontSize: "16px", color: "hsl(178, 93%, 10%)", fontFamily: "Segoe UI Bold, sans-serif, system-ui" }}>
                            <FormattedMessage id="recruiter.cv_theque.map_teaser" defaultMessage="See on a map where candidates live and where they want to work." />
                          </Typography>
                          <Typography sx={{ fontSize: "13px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                            <FormattedMessage id="credits_box.no_cv_access" />
                          </Typography>
                          <Button
                            href={`/${i18nLocale}/recruiter/pricing`}
                            onClick={() => trackEvent("cvtheque", "map_teaser_clicked")}
                            sx={{
                              mt: "4px", px: "24px", py: "8px", borderRadius: "999px", textTransform: "none",
                              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                              bgcolor: "hsl(178, 93%, 10%)", color: "hsl(0, 0%, 100%)",
                              "&:hover": { bgcolor: "hsl(178, 93%, 16%)", color: "hsl(0, 0%, 100%)" },
                            }}
                          >
                            <FormattedMessage id="order.order" />
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box id="cvtheque-candidate-scroll-anchor">
                      <CVThequeCandidateDetail
                        selectedCandidate={selectedCandidate}
                        setSelectedCandidate={setSelectedCandidate}
                        resumeLibraryCredit={resumeLibraryCredit}
                        setKanban={setKanban}
                        scrollToTopOnCandidateChange={firstSelected}
                      />
                    </Box>
                  )}
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>

      <FiltersCandidatesModal
        open={filtersCandidatesOpen}
        onClose={() => setFiltersCandidatesOpen(false)}
        candidatesCount={count ?? 0}
        appliedFilters={{
          onlyFavorites,
          languages: selectedLanguages,
          sectors: selectedSectors,
          availabilities: selectedAvailabilities,
          experienceRanges: selectedExperiences,
          hardSkills: selectedHardSkills,
          softSkills: selectedSoftSkills,
          locations: selectedLocations,
          radiusKm: selectedRadiusKm,
          highestDegrees: selectedHighestDegrees,
          candidateStatuses: selectedCandidateStatuses,
          activeWithin: selectedActiveWithin,
          booleanClauses,
          booleanExpression,
        }}
        onApply={(next) => {
          setSelectedLocations(next.locations || []);
          setSelectedRadiusKm((next.locations || []).length > 0 ? next.radiusKm || 0 : 0);
          setOnlyFavorites(Boolean(next.onlyFavorites));
          setSelectedLanguages(next.languages || []);
          setSelectedSectors(next.sectors || []);
          setSelectedAvailabilities(next.availabilities || []);
          setSelectedExperiences(next.experienceRanges || []);
          setSelectedHardSkills(next.hardSkills || []);
          setSelectedSoftSkills(next.softSkills || []);
          setSelectedHighestDegrees(next.highestDegrees || []);
          setSelectedCandidateStatuses(next.candidateStatuses || []);
          setSelectedActiveWithin(next.activeWithin || null);
          setBooleanClauses(next.booleanClauses || []);
          setBooleanExpression(typeof next.booleanExpression === "string" ? next.booleanExpression : "");

        }}
      />
    </Box>
  );
};

export default CVThequeContent;
